import { mergeAttributes, Node, ReactNodeViewRenderer } from "@tiptap/react";
import {
  CONDITIONAL_INLINE_ATTR,
  CONDITIONAL_NODE_NAME,
  CONDITIONAL_TEMPLATE_ATTR,
} from "./constants";
import { ConditionalNodeView } from "./conditional-node";

export interface ConditionalNodeOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    conditional: {
      /**
       * Insert a conditional node holding the given raw `{{#if}}…{{/if}}` template.
       * Pass an empty string to drop in a fresh, unconfigured conditional.
       */
      insertConditional: (template: string) => ReturnType;
    };
  }
}

/**
 * Inline, atomic node representing an `{{#if}}…{{/if}}` conditional (with its
 * `{{else if}}` / `{{else}}` chain). Stores only the raw template string + a
 * block/inline flag; the node view parses it into editable branches and evaluates
 * the active branch live via the dynamic node context.
 */
export const ConditionalNode = Node.create<ConditionalNodeOptions>({
  name: CONDITIONAL_NODE_NAME,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      [CONDITIONAL_TEMPLATE_ATTR]: {
        default: "",
        parseHTML: (element) =>
          element.getAttribute(`data-${CONDITIONAL_TEMPLATE_ATTR}`) ?? "",
        renderHTML: (attributes) => ({
          [`data-${CONDITIONAL_TEMPLATE_ATTR}`]:
            attributes[CONDITIONAL_TEMPLATE_ATTR],
        }),
      },
      [CONDITIONAL_INLINE_ATTR]: {
        default: true,
        parseHTML: (element) =>
          element.getAttribute(`data-${CONDITIONAL_INLINE_ATTR}`) !== "false",
        renderHTML: (attributes) => ({
          [`data-${CONDITIONAL_INLINE_ATTR}`]: String(
            attributes[CONDITIONAL_INLINE_ATTR]
          ),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${CONDITIONAL_NODE_NAME}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { "data-type": CONDITIONAL_NODE_NAME },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ConditionalNodeView);
  },

  addCommands() {
    return {
      insertConditional:
        (template: string) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { [CONDITIONAL_TEMPLATE_ATTR]: template },
          }),
    };
  },
});

export default ConditionalNode;
